const CONSENT_STORAGE_KEY = 'smartmenu.cookieConsent';
const CONSENT_COOKIE_KEY = 'smartmenu_cookie_consent';
const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 400;

export const CONSENT_STATUS = {
  GRANTED: 'granted',
  DENIED: 'denied'
};

const isValidStatus = (status) => Object.values(CONSENT_STATUS).includes(status);

const readStoredValue = (value) => {
  if (!value) return null;
  // Migrate the plain granted/denied value stored by earlier releases.
  if (isValidStatus(value)) return value;
  try {
    const record = JSON.parse(value);
    return record?.version === CONSENT_VERSION && isValidStatus(record.status) ? record.status : null;
  } catch {
    return null;
  }
};

const readConsentCookie = () => {
  if (typeof document === 'undefined') return null;
  const prefix = `${CONSENT_COOKIE_KEY}=`;
  const entry = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(prefix));
  return entry ? readStoredValue(decodeURIComponent(entry.slice(prefix.length))) : null;
};

const writeConsentCookie = (status) => {
  if (typeof document === 'undefined') return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CONSENT_COOKIE_KEY}=${encodeURIComponent(status)}; Path=/; Max-Age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
};

export const getConsentStatus = () => {
  if (typeof window === 'undefined') return null;
  try {
    return readStoredValue(localStorage.getItem(CONSENT_STORAGE_KEY)) || readConsentCookie();
  } catch (error) {
    console.error('Failed to read cookie consent status', error);
    return readConsentCookie();
  }
};

export const hasConsent = () => getConsentStatus() === CONSENT_STATUS.GRANTED;
export const hasConsentDecision = () => isValidStatus(getConsentStatus());

export const storeConsentStatus = (status) => {
  if (typeof window === 'undefined' || !isValidStatus(status)) return;
  const record = JSON.stringify({ status, version: CONSENT_VERSION, decidedAt: new Date().toISOString() });
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, record);
  } catch (error) {
    console.error('Failed to store cookie consent status', error);
  } finally {
    writeConsentCookie(status);
  }
};

export const revokeConsent = () => storeConsentStatus(CONSENT_STATUS.DENIED);
